import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { toast } from 'react-toastify';

import { selectUserRolePermissions } from '@socialive/common';
import AccountNav from '../../components/AccountNav';
import ConfirmationModal from '../../components/ConfirmationModal';
import ConnectionManagerModal from '../../components/ConnectionManagerModal';
import CustomTable, { ConnectionTableRowRenderer } from '../../components/CustomTable';
import EmptyResult from '../../components/EmptyResult';
import Loader from '../../components/Loader';
import TrayEmbeddedModal from '../../components/TrayEmbeddedModal';
import createRequestBodyFactory from '../../helpers/connectionManager/createRequestBodyFactory';
import generateNewConnection from '../../helpers/connectionManager/generateNewConnection';
import updateRequestBodyFactory from '../../helpers/connectionManager/updateRequestBodyFactory';
import { ConnectionManagerNetworkKeys } from '../../helpers/constants';
import dynamicallySort from '../../helpers/dynamicallySort';
import trackMixPanelEvent from '../../helpers/trackMixPanelEvent';
import {
  createConnection,
  deleteConnection,
  getAllConnections,
  updateConnection,
} from '../../services/connectionManager';
import {
  deleteTraySolutionInstance,
  newTrayInstanceWizard,
  renameTraySolutionInstance,
  selectInProgressTrayInstance,
  selectIsTrayLoading,
  selectTrayInstances,
  updateTrayInstanceWizard,
} from '../../store/connectionManager';

import { selectMaxConnectionLimit } from '../../store/accountInfo';

import './ConnectionManager.css';

const tableHeaders = [
  { sort: 'title', displayName: 'DESTINATION' },
  { sort: '', displayName: '' },
  { sort: 'actions', displayName: 'ACTIONS', disableSorting: true },
];

const ConnectionManager = ({ APIBaseURL, accountId, checkAuthorization }) => {
  const dispatch = useDispatch();
  const inProgressTrayInstance = useSelector(selectInProgressTrayInstance);
  const trayInstances = useSelector(selectTrayInstances);
  const allowedConnectionsLimit = useSelector(selectMaxConnectionLimit);
  const isTrayLoading = useSelector(selectIsTrayLoading);
  const { canManageIntegrations } = useSelector(selectUserRolePermissions);
  const history = useHistory();
  const [isIntegrationLoading, setIsIntegrationLoading] = useState(true);
  const [isConnectionModalOpen, setIsConnectionModalOpen] = useState(false);
  const [isTrayModalOpen, setIsTrayModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [isModalUpdateAction, setIsModalUpdateAction] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [connections, setConnections] = useState([]);
  const [sortElement, setSortElement] = useState('');
  const [sortDirection, setSortDirection] = useState('');

  const allConnections = useMemo(() => [...connections, ...trayInstances], [trayInstances, connections]);

  const closeModal = useCallback(() => {
    setIsRemoveModalOpen(false);
    setIsConnectionModalOpen(false);
    setIsTrayModalOpen(false);
    setSelectedConnection(null);
  }, []);

  const onAddNewConnectionClicked = () => {
    if (allConnections.length >= allowedConnectionsLimit) {
      toast.warn('Your request exceeds the number of connections allowed for your account');
      return;
    }

    setIsConnectionModalOpen(true);
    setIsModalUpdateAction(false);
  };

  const onConnectionFormChange = (newConnection) => {
    setSelectedConnection(newConnection);
  };

  const onConnectionCreate = async () => {
    if (selectedConnection.network.type === ConnectionManagerNetworkKeys.TRAY) {
      const { title, network } = selectedConnection;
      dispatch(newTrayInstanceWizard({ solutionId: network?.id, accountId, title }));
      closeModal();
      return;
    }

    const requestBody = createRequestBodyFactory(selectedConnection, accountId);
    const { network } = selectedConnection;

    const { error, newConnection } = await createConnection({ APIBaseURL, network, requestBody });

    if (error) {
      toast.error(error.message);
      return;
    }

    const connectionTitle = newConnection.title;
    trackMixPanelEvent('Add integration', { 'Integration Title': connectionTitle });
    toast.success(`${connectionTitle} was added.`);

    setConnections((current) => [...current, newConnection]);
    closeModal();
  };

  const executeConnectionUpdate = useCallback(
    async (requestBody) => {
      const { network } = selectedConnection;

      const { error, updatedConnection } = await updateConnection({
        APIBaseURL,
        updates: requestBody,
        network,
        resourceId: selectedConnection.id,
      });

      if (error) {
        toast.error(error.message);
        return;
      }

      const ConnectionTitle = updatedConnection.title;
      toast.success(`${ConnectionTitle} was updated.`);

      setConnections((prevConnections) =>
        prevConnections.map((prevConnection) => {
          if (prevConnection.id === updatedConnection.id) {
            return { ...updatedConnection };
          }

          return prevConnection;
        })
      );

      closeModal();
    },
    [APIBaseURL, selectedConnection, closeModal]
  );

  const onConnectionUpdate = () => {
    if (selectedConnection.network.type === ConnectionManagerNetworkKeys.TRAY) {
      const { title, id } = selectedConnection;
      dispatch(renameTraySolutionInstance({ id, title }));
      closeModal();
      return;
    }

    const requestBody = updateRequestBodyFactory(selectedConnection);
    executeConnectionUpdate(requestBody);
  };

  const onChannelSelected = async (network) => {
    const newConnection = generateNewConnection({ network, title: network.title });
    setSelectedConnection(newConnection);
  };

  const onEditConnectionClicked = async (connection) => {
    if (connection?.network?.type === ConnectionManagerNetworkKeys.PIGEON_HOLE) {
      setSelectedConnection(connection);
    }

    if (connection?.network?.type === ConnectionManagerNetworkKeys.TRAY) {
      const { error } = await dispatch(updateTrayInstanceWizard({ solutionInstance: connection }));
      if (error) return;
    }

    setIsConnectionModalOpen(true);
    setIsModalUpdateAction(true);
  };

  const onRenameTrayIntegrationClicked = async (connection) => {
    setSelectedConnection(connection);
    setIsConnectionModalOpen(true);
    setIsModalUpdateAction(true);
  };

  const onRemoveConnectionClicked = (connection) => {
    setSelectedConnection(connection);
    setIsRemoveModalOpen(true);
  };

  const onRemoveConnectionConfirmed = async () => {
    const connectionTitle = selectedConnection.title;

    if (selectedConnection?.network?.type === ConnectionManagerNetworkKeys.PIGEON_HOLE) {
      const { error } = await deleteConnection({
        APIBaseURL,
        networkType: selectedConnection?.network?.type,
        hash: selectedConnection.hash,
      });

      if (error) {
        toast.error(error.message);
        closeModal();
        return;
      }

      trackMixPanelEvent('Remove integration', { 'Integration Title': connectionTitle });
      toast.success(`${connectionTitle} was removed.`);

      setConnections((prevConnections) =>
        prevConnections.filter((prevConnection) => prevConnection.id !== selectedConnection.id)
      );

      closeModal();
    }

    if (selectedConnection?.network?.type === ConnectionManagerNetworkKeys.TRAY) {
      const { error } = await dispatch(
        deleteTraySolutionInstance({ solutionInstanceId: selectedConnection.id, instanceTitle: connectionTitle })
      );

      if (error) {
        toast.error(`Unable to remove "${connectionTitle}". Please try again.`);
      } else {
        toast.success(`${connectionTitle} was removed.`);
      }
      closeModal();
    }
  };

  const handleChannelsSort = (sortBy) => {
    const newSortDirection = sortBy === sortElement && sortDirection === 'asc' ? 'desc' : 'asc';

    setConnections((currentConnections) => currentConnections.sort(dynamicallySort(sortBy, newSortDirection)));
    setSortElement(sortBy);
    setSortDirection(newSortDirection);
  };

  const generateModalTitle = useCallback((isUpdateAction, IsTrayRenamingAction) => {
    if (IsTrayRenamingAction) return 'Rename Integration';
    return isUpdateAction ? 'Edit Integration' : 'Add Integration';
  }, []);

  useEffect(() => {
    checkAuthorization('integrations/');
  }, [checkAuthorization]);

  useEffect(() => {
    if (accountId && accountId !== 'null' && !canManageIntegrations) {
      history.push('/dashboard');
    }
  }, [accountId, history, canManageIntegrations]);

  useEffect(() => {
    const fetchConnections = async () => {
      const connectionsResponse = await getAllConnections({ APIBaseURL, accountId });

      setConnections(connectionsResponse);
      setIsIntegrationLoading(false);
    };

    setIsIntegrationLoading(true);

    fetchConnections();
  }, [APIBaseURL, accountId]);

  useEffect(() => {
    if (inProgressTrayInstance.success) {
      setSelectedConnection(inProgressTrayInstance.instance);
      setIsTrayModalOpen(true);
    }
    if (!inProgressTrayInstance.loading && inProgressTrayInstance.success === false) {
      toast.error(`Something went wrong. Please try again.`);
    }
  }, [inProgressTrayInstance]);

  const onSaveAction = isModalUpdateAction ? onConnectionUpdate : onConnectionCreate;
  const isTrayRenaming = selectedConnection?.network?.type === ConnectionManagerNetworkKeys.TRAY && isModalUpdateAction;
  const modalTitle = generateModalTitle(isModalUpdateAction, isTrayRenaming);
  const modalIcon = isModalUpdateAction ? <i className="icon-pencil-edit circle" /> : <i className="icon-circle-add" />;
  const hasConnections = allConnections.length > 0;
  const canHaveConnections = allowedConnectionsLimit > 0;
  const isLoading = isTrayLoading || isIntegrationLoading;

  return (
    <div className="page-content content-container">
      <AccountNav />
      <div className="header-container">
        <div>
          <h2 className="header-container-title">Integrations</h2>
          <p className="header-container-subtitle">
            {allConnections.length} of {allowedConnectionsLimit}{' '}
            {allowedConnectionsLimit === 1 ? 'integration' : 'integrations'} configured
          </p>
        </div>
        {canHaveConnections && (
          <button type="button" className="btn text-bold" onClick={onAddNewConnectionClicked}>
            Add Integration
          </button>
        )}
      </div>
      {isLoading && !hasConnections && <Loader />}

      {!isLoading && !hasConnections && canHaveConnections && (
        <EmptyResult imgSrc="/no-connections.png" title="No Integrations Added for your Account" />
      )}

      {!isLoading && !hasConnections && !canHaveConnections && (
        <EmptyResult imgSrc="/no-connections.png" title="No Integrations Enabled for your Account" />
      )}

      {hasConnections && (
        <div className="connections-table">
          <CustomTable
            headerItems={tableHeaders}
            handleSort={handleChannelsSort}
            sortDirection={sortDirection}
            selectedHeaderItem={sortElement}
            isLoading={isLoading}
            tableBody={allConnections.map((item) => (
              <ConnectionTableRowRenderer
                key={item.id}
                item={item}
                onEditOption={onEditConnectionClicked}
                onRenameOption={onRenameTrayIntegrationClicked}
                onRemoveOption={onRemoveConnectionClicked}
              />
            ))}
          />
        </div>
      )}

      {isTrayModalOpen && selectedConnection?.network?.type === ConnectionManagerNetworkKeys.TRAY && (
        <TrayEmbeddedModal isOpen={isTrayModalOpen} instance={selectedConnection} handleOnClose={closeModal} />
      )}

      {isConnectionModalOpen && !isTrayModalOpen && (
        <ConnectionManagerModal
          isOpen={isConnectionModalOpen}
          headerIcon={modalIcon}
          isEditTab={Boolean(selectedConnection)}
          connection={selectedConnection}
          modalTitle={modalTitle}
          handleOnClose={closeModal}
          handleOnChannelSelected={onChannelSelected}
          handleOnConnectionFormChange={onConnectionFormChange}
          handleOnSave={onSaveAction}
          showBreadcrumbNav={!isTrayRenaming}
        />
      )}

      {selectedConnection && isRemoveModalOpen && (
        <ConfirmationModal
          isOpen={isRemoveModalOpen}
          handleOnClose={closeModal}
          handleOnConfirm={onRemoveConnectionConfirmed}
          confirmButtonText="Remove"
          customClass="connection-remove-modal"
          resourceDescription={
            <div className="connection-manager-delete-body">
              <p>Do you really want to remove</p>
              <div className="network-info">
                <img
                  className="logo"
                  src={selectedConnection?.network?.logo}
                  alt={`${selectedConnection?.network?.title} logo`}
                />
                <div>
                  <p className="title">{selectedConnection?.title}</p>
                </div>
              </div>
            </div>
          }
        />
      )}
    </div>
  );
};

ConnectionManager.propTypes = {
  accountId: PropTypes.string.isRequired,
  APIBaseURL: PropTypes.string.isRequired,
  checkAuthorization: PropTypes.func.isRequired,
};

export default ConnectionManager;
